import { Action, Alert, Badge, Card } from 'corso-admin-ui';
import { claimSourceName, isTruthy } from 'corso-types';
import { formatter } from 'corso-web-lib';
import { Key, useState } from 'react';
import DescriptionList from '~/components/DescriptionList';
import { MediaGallery } from '~/components/media/MediaGallery';
import {
  List,
  ListItem,
} from '~/features/claimReview/components/summary/ClaimSummaryList';
import { ExchangeOrderLine } from '~/components/lineItems/ExchangeOrderLine';
import { RefundLineItem } from '~/components/lineItems/RefundLineItem';
import { useCanEditShippingClaim } from '~/features/shippingClaimReview/hooks/useCanEditShippingClaim';
import { useShippingClaim } from '~/features/shippingClaimReview/ShippingClaimReviewProvider';
import { useCurrency } from '~/hooks/useCurrency';
import { useIsCorsoCxAgent } from '~/hooks/useIsCorsoAdmin';
import { useStoreUser } from '~/providers/StoreUserProvider';
import {
  shippingClaimReasonName,
  shippingClaimResolutionMethodName,
} from '~/utils/enumNameMaps';
import { EditShippingClaim } from './EditShippingClaim';

type UnifiedSummaryLineItem = {
  id: Key;
  name: string | null;
  imgUrl: string | null;
  quantity: number;
  optionsFromPlatform: { value: string; name: string }[];
  unitPrice: number | null;
  unitTax: number | null;
  sku?: string | null;
};

type UnifiedSummary = {
  exchangeOrders: {
    id: Key;
    orderNameFromPlatform?: string;
    platformAdminUrl?: string;
    lineItems: UnifiedSummaryLineItem[];
    containsShopNowItems: boolean;
    wasResolvedExternally: boolean;
    isOriginalOrder: boolean;
  }[];
  refunds: {
    id: Key;
    amount: number;
    wasResolvedExternally: boolean;
  }[];
};

function unifiedSummary(
  shippingClaim: ReturnType<typeof useShippingClaim>,
): UnifiedSummary {
  const { resolutionSummary } = shippingClaim;
  const exchangeOrders: UnifiedSummary['exchangeOrders'] = [];
  const refunds: UnifiedSummary['refunds'] = [];

  if (resolutionSummary?.exchangeOrder) {
    const { exchangeOrder } = resolutionSummary;
    exchangeOrders.push({
      id: exchangeOrder.id,
      orderNameFromPlatform: exchangeOrder.orderNameFromPlatform,
      platformAdminUrl: exchangeOrder.platformAdminUrl,
      containsShopNowItems: false,
      wasResolvedExternally: false,
      isOriginalOrder: false,
      lineItems: exchangeOrder.lineItems.map((lineItem) => ({
        id: lineItem.id,
        name: lineItem.name,
        imgUrl: lineItem.imgUrl,
        quantity: lineItem.quantity,
        optionsFromPlatform: lineItem.optionsFromPlatform,
        unitPrice: lineItem.unitPrice !== 0 ? lineItem.unitPrice : null,
        unitTax: lineItem.unitTax !== 0 ? lineItem.unitTax : null,
        sku: lineItem.sku,
      })),
    });
  }

  if (resolutionSummary?.refund) {
    refunds.push({
      id: 'refund',
      amount: resolutionSummary.refund.amount,
      wasResolvedExternally: false,
    });
  }

  return { exchangeOrders, refunds };
}

function ShippingClaimSummary() {
  const shippingClaim = useShippingClaim();
  const summary = unifiedSummary(shippingClaim);

  const summaryHasItems = Object.values(summary).some(
    (items) => items.length > 0,
  );

  // TODO: determine if we want to show the alert of "nothing issued yet / etc"
  if (!summaryHasItems) return null;

  return (
    <Card flush>
      <div className="p-4 pb-0">
        <Card.Line>
          <Card.Heading>Resolution Summary</Card.Heading>
        </Card.Line>
      </div>

      <List className="*:p-4 *:first:pt-0">
        {summary.exchangeOrders.map((order) => (
          <ListItem key={order.id}>
            <ExchangeOrderLine order={order} />
          </ListItem>
        ))}
        {summary.refunds.map((refund) => (
          <ListItem key={refund.id}>
            <RefundLineItem refund={refund} />
          </ListItem>
        ))}
      </List>
    </Card>
  );
}

// TODO revisit `max-w-prose` once notes have real formatting; it's a readability cap for long free-text notes
function NoteText({ note }: { note: string }) {
  return (
    <p className="max-w-prose wrap-anywhere whitespace-pre-wrap">{note}</p>
  );
}

export function ShippingClaimRequest() {
  const {
    resolutionMethod,
    noteFromCustomer,
    reason,
    source,
    images,
    noteFromMerchant,
    financialSummary,
    hasBeenInvoiced,

    originalStoreOrder: { wasShippingProtected },
  } = useShippingClaim();

  const { currencyCode } = useCurrency();
  const canEdit = useCanEditShippingClaim();
  const isCorsoCxAgent = useIsCorsoCxAgent();
  const storeUser = useStoreUser();
  const [showEdit, setShowEdit] = useState(false);

  const descriptions = [
    { term: 'Store', details: storeUser.store.name },
    {
      term: 'Protected',
      details: wasShippingProtected ? (
        <Badge variant="success">Yes</Badge>
      ) : (
        <Badge variant="danger">No</Badge>
      ),
    },

    {
      term: 'Resolution Method',
      details: shippingClaimResolutionMethodName[resolutionMethod],
    },
    { term: 'Reason', details: shippingClaimReasonName[reason] },
    {
      term: 'Source',
      details: source ? claimSourceName[source] : 'Customer Portal',
    },
    ...(isCorsoCxAgent
      ? [
          { term: 'Invoiced', details: hasBeenInvoiced ? 'Yes' : 'No' },

          {
            term: 'Total Value',
            details: formatter.currency(
              financialSummary.totalValue,
              currencyCode,
            ),
          },
          {
            term: 'Refundable on Shopify',
            details:
              financialSummary.refundableAmount === undefined
                ? 'Unavailable'
                : formatter.currency(
                    financialSummary.refundableAmount,
                    currencyCode,
                  ),
          },
        ]
      : []),
    ...(noteFromCustomer
      ? [{ term: 'Note', details: <NoteText note={noteFromCustomer} /> }]
      : []),
  ].filter(isTruthy);

  return (
    <>
      <ShippingClaimSummary />
      <Card>
        <Card.Line>
          <Card.Heading>Shipping Claim Request</Card.Heading>
          <div>
            {canEdit && (
              <Action
                icon="edit"
                variant="ghost"
                accessibilityLabel="Edit Claim"
                onClick={() => setShowEdit(true)}
              />
            )}
          </div>
        </Card.Line>

        <DescriptionList descriptions={descriptions} />

        {noteFromMerchant && (
          <>
            <Card.Heading>Merchant Note</Card.Heading>
            <Alert heading={noteFromMerchant} variant="default" />
          </>
        )}

        {images && images.length > 0 && (
          <>
            <Card.Heading>Customer Media</Card.Heading>
            <MediaGallery
              title="Customer Media"
              items={images.map((url, index) => ({
                src: url,
                alt: `Customer Media ${index + 1}`,
              }))}
            />
          </>
        )}

        {canEdit && (
          <EditShippingClaim
            show={showEdit}
            onClose={() => setShowEdit(false)}
          />
        )}
      </Card>
    </>
  );
}
